import clsx from 'clsx';

import Flex from '@commons/containers/Flex';
import Grid from '@commons/containers/Grid';
import ResponsiveContainer from '@commons/containers/ResponsiveContainer';
import PageBuilder from '@commons/pagebuilder';
import type { DrupalSection } from '@commons/types/drupal';
import type { Breakpoint, ResponsiveStyle } from '@commons/types/styles';
import type { Alignment, Spacing, Theme, Width } from '@commons/types/styles';

import { Background, type BackgroundSettings } from '../Background';
import { type SectionMinHeightType, getSectionMinHeight } from '../helpers';
import Region from './Region';
import styles from './index.module.scss';

// Regions of the banner section.

const BANNER_SECTION_REGIONS = ['main', 'bottomLeft', 'bottomRight'];

// Type definition for the BannerSection component properties.

type Props = DrupalSection<{
  appearance: BackgroundSettings & {
    theme: Theme;
  };
  container: {
    contentWidth: Width;
    paddingTop: Spacing;
    paddingBottom: Spacing;
    horizontalAlignment: Alignment;
    minHeightType: SectionMinHeightType;
    customMinHeight?: string;
    fluid?: boolean;
  };
  layout: {
    rowGap: Spacing;
    columnGap: Spacing | 'max';
    mobileBreakpoint: Breakpoint;
  };
}>;

/**
 * Banner section component.
 *
 * IMPORTANT:
 * - This component should be a server component, otherwise everything it renders and itself will be client components.
 *   Since it is used on many page, this would bloat the JavaScript bundle significantly.
 */

const BannerSection = ({ type, blocks, weight, settings }: Props) => {
  const { appearance, container, layout } = settings;

  const {
    contentWidth,
    paddingTop,
    paddingBottom,
    horizontalAlignment,
    minHeightType,
    customMinHeight,
    fluid,
  } = container;

  const { rowGap, columnGap, mobileBreakpoint } = layout;

  const responsiveContentWidth: ResponsiveStyle<Width> = {
    xs: 12,
    [mobileBreakpoint]: contentWidth,
  };

  const minHeight = getSectionMinHeight(minHeightType, customMinHeight);

  const sectionData = { type, weight };
  const blocksByRegions = PageBuilder.filterBlocksByRegions(
    blocks,
    BANNER_SECTION_REGIONS as string[]
  );

  const renderedRegions = BANNER_SECTION_REGIONS.map((region) => {
    const isMainRegion = region === 'main';
    const renderedBlocks = blocksByRegions[region].map(PageBuilder.renderBlock);

    return PageBuilder.renderRegion(
      region,
      Region,
      {
        width: isMainRegion ? 12 : { xs: 12, md: 8, [mobileBreakpoint]: 4 },
        children: renderedBlocks,
        className: styles[region],
      },
      sectionData
    );
  });

  const isPriority = weight === 0;
  const renderedBackground = <Background priority={isPriority} {...appearance} />;

  return (
    <ResponsiveContainer
      fluid={fluid}
      theme={appearance.theme}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      horizontalAlignment={horizontalAlignment}
      outsideChildren={renderedBackground}
      className={clsx(styles.bannerSection, styles[`mobile-${mobileBreakpoint}`])}
      style={{ minHeight }}
    >
      <Grid columns={1} width={responsiveContentWidth} rowGap={rowGap} className={styles.regions}>
        {renderedRegions[0]}
        <Flex
          gap={columnGap === 'max' ? 'sm' : columnGap}
          align={{ xs: 'center', [mobileBreakpoint]: 'end' }}
          justify={{ xs: 'center', [mobileBreakpoint]: 'space-between' }}
          direction={{ xs: 'column', [mobileBreakpoint]: 'row' }}
          className={styles.bottomRegions}
        >
          {renderedRegions[1]}
          {renderedRegions[2]}
        </Flex>
      </Grid>
    </ResponsiveContainer>
  );
};

export default BannerSection;
